import React, { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
// Section component for consistent styling (same as owner view)
const Section = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => <section className="pt-16 sm:pt-20" data-id="element-2424">
    <h2 className="text-lg sm:text-xl text-[#F5F5F5] mb-8" style={{
    fontFamily: 'Georgia, serif'
  }} data-id="element-2425">
      {title}
    </h2>
    <div className="space-y-5 text-base sm:text-lg text-gray-400 leading-[1.8]" data-id="element-2426">
      {children}
    </div>
  </section>;
export function LifeDecodeShared() {
  const navigate = useNavigate();
  const handleExploreOwn = () => {
    navigate('/decode/entry');
  };
  const handleClose = () => {
    window.close();
    // Fallback if window.close() doesn't work
    navigate('/');
  };
  return <>
      <Helmet data-id="element-2427">
        <title data-id="element-2428">Life Decode | Shared with you</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-2429" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2430">
        {/* Minimal header - no full nav */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2431">
          <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2432">
            <span className="text-sm text-gray-500" style={{
            fontFamily: 'Georgia, serif'
          }} data-id="element-2433">
              Life Decode
            </span>
            <span className="text-xs text-gray-600" data-id="element-2434">
              Private view · Shared intentionally
            </span>
          </div>
        </header>

        <main className="pt-20 sm:pt-24 pb-32 sm:pb-40" data-id="element-2435">
          <div className="max-w-2xl mx-auto px-6 sm:px-8" data-id="element-2436">
            {/* ENTRY HEADER */}
            <header className="py-12 sm:py-16" data-id="element-2437">
              <h1 className="text-2xl sm:text-3xl text-[#F5F5F5] mb-3" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2438">
                Life Decode
              </h1>
              <p className="text-sm text-gray-600" data-id="element-2439">Shared privately with you</p>
            </header>

            {/* CONTEXT SETTING BLOCK — Critical */}
            <div className="pb-12 sm:pb-16 border-b border-[#2B2B2B]" data-id="element-2440">
              <div className="space-y-4 text-sm text-gray-500 leading-relaxed" data-id="element-2441">
                <p data-id="element-2442">Someone who knows you shared this with you.</p>
                <p data-id="element-2443">
                  This is a personal decode they chose to make visible to you.
                  There is nothing you need to do here.
                </p>
              </div>
            </div>

            {/* CONTENT — Same as owner view, full and untruncated */}

            {/* SECTION 1 — How this pattern formed */}
            <Section title="How this pattern formed" data-id="element-2444">
              <p data-id="element-2445">
                The structure you operate from did not begin with you. It was
                shaped by the environment you were born into — the pressures,
                the silences, the unspoken rules of your early world.
              </p>
              <p data-id="element-2446">
                You learned early that certain ways of being were rewarded, and
                others were not. This was not instruction. It was absorption.
              </p>
              <p data-id="element-2447">
                The pattern that emerged was not a choice. It was an adaptation
                — a way of surviving the specific conditions you found yourself
                in.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2448">
                This is the origin layer. Everything else builds on top of it.
              </p>
            </Section>

            {/* SECTION 2 — Generational pressures */}
            <Section title="The generational pressures you inherited" data-id="element-2449">
              <p data-id="element-2450">
                Your parents carried their own unresolved structures. Their
                fears, their ambitions, their unlived lives — all of this was
                transmitted to you, often without words.
              </p>
              <p data-id="element-2451">
                You may have been given a role before you could speak: the one
                who would succeed, the one who would stay close, the one who
                would carry what they could not.
              </p>
              <p data-id="element-2452">
                These pressures are not visible in memory. They are visible in
                pattern — in what you feel compelled to do, even when it costs
                you.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2453">
                You did not choose this inheritance. But you have been living
                inside it.
              </p>
            </Section>

            {/* SECTION 3 — Environments that shaped pace and direction */}
            <Section title="The environments that shaped your pace and direction" data-id="element-2454">
              <p data-id="element-2455">
                Every environment you entered — school, work, relationships —
                either reinforced your pattern or created friction against it.
              </p>
              <p data-id="element-2456">
                When the environment matched your wiring, you accelerated. When
                it didn't, you either adapted or withdrew.
              </p>
              <p data-id="element-2457">
                The places where you thrived were not random. They were
                structurally compatible with how you were already organized.
              </p>
              <p data-id="element-2458">
                The places where you struggled were not failures. They were
                mismatches — environments that demanded a shape you could not
                hold.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2459">
                Your trajectory was not about effort. It was about fit.
              </p>
            </Section>

            {/* SECTION 4 — Phases of expansion, correction, settling */}
            <Section title="The phases where this structure expanded, corrected, and settled" data-id="element-2460">
              <p data-id="element-2461">
                Your life has moved through distinct phases — not random, but
                structurally inevitable.
              </p>
              <p data-id="element-2462">
                There were periods of expansion, where your pattern was rewarded
                and you moved forward with momentum.
              </p>
              <p data-id="element-2463">
                There were periods of correction, where the pattern hit its
                limits and something had to give.
              </p>
              <p data-id="element-2464">
                And there were periods of settling, where you integrated what
                you had learned and the pace slowed.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2465">
                These phases were not accidents. They were the structure
                expressing itself across time.
              </p>
            </Section>

            {/* SECTION 5 — What this phase is no longer willing to support */}
            <Section title="What this phase is no longer willing to support" data-id="element-2466">
              <p data-id="element-2467">
                You are now in a phase where certain things that once worked are
                no longer sustainable.
              </p>
              <p data-id="element-2468">
                The compromises you made earlier — the roles you held, the pace
                you maintained, the parts of yourself you suppressed — are
                becoming harder to carry.
              </p>
              <p data-id="element-2469">
                This is not a crisis. It is a structural shift. The pattern is
                asking for something different.
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2470">
                What you do with this information is yours to decide. But the
                structure will not wait indefinitely.
              </p>
            </Section>

            {/* SECTION 6 — What changes when you see the whole structure */}
            <Section title="What changes when you see the whole structure" data-id="element-2471">
              <p data-id="element-2472">
                Seeing this does not fix anything. It does not remove the
                pattern or make the trade-offs disappear.
              </p>
              <p data-id="element-2473">
                But it changes your relationship to what is happening. The
                confusion reduces. The urgency softens.
              </p>
              <p data-id="element-2474">
                You stop asking "why is this happening to me?" and start seeing
                "this is how I was organized to respond."
              </p>
              <p className="text-[#F5F5F5]" data-id="element-2475">
                The pattern doesn't disappear. It becomes something you own.
              </p>
            </Section>

            {/* POST-READ ZONE — Gentle, no pressure */}
            <section className="pt-24 sm:pt-32 border-t border-[#2B2B2B] mt-16 sm:mt-20" data-id="element-2476">
              {/* TRANSITION COPY */}
              <div className="space-y-4 text-sm text-gray-500 leading-relaxed mb-12" data-id="element-2477">
                <p data-id="element-2478">
                  If this resonated, you might be curious about your own
                  patterns.
                </p>
                <p data-id="element-2479">That exploration is personal, and optional.</p>
              </div>

              {/* OPTIONAL ACTIONS — Very quiet */}
              <div className="space-y-6" data-id="element-2480">
                {/* PRIMARY — Soft, not dominant */}
                <button onClick={handleExploreOwn} className="px-6 py-3 border border-[#2B2B2B] text-gray-400 text-sm hover:border-gray-500 hover:text-[#F5F5F5] transition-colors" data-id="element-2481">
                  Explore my own Decode
                </button>

                {/* SECONDARY — Permission to leave */}
                <div data-id="element-2482">
                  <button onClick={handleClose} className="text-sm text-gray-600 hover:text-gray-500 transition-colors" data-id="element-2483">
                    Close this page
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </>;
}